/**
 * Shared graph configuration — single source of truth for all graph components.
 */

import type { PhysicsPreset } from '../../types/graph';

// Faculty colors
export const FACULTY_COLORS: Record<string, string> = {
  'FSv':   '#0088FE',
  'KU':    '#16A085',
  'FBMI':  '#F4D35E',
  'FS':    '#1CA9C9',
  'FA':    '#A685E2',
  'CIIRC': '#6BCB77',
  'FEL':   '#FFD885',
  'UCEEB': '#62B6CB',
  'FD':    '#B5E48C',
  'UTEF':  '#8980F5',
  'MUVS':  '#E57373',
  'FJFI':  '#F5A962',
  'FIT':   '#00B2CA',
};

export const DEFAULT_NODE_COLOR = '#999999';

// Department color palette (20 distinct saturated hues)
export const DEPARTMENT_PALETTE: readonly string[] = [
  '#2563EB', '#DC2626', '#059669', '#D97706', '#7C3AED',
  '#DB2777', '#0891B2', '#65A30D', '#EA580C', '#4F46E5',
  '#0D9488', '#CA8A04', '#9333EA', '#E11D48', '#0284C7',
  '#16A34A', '#C2410C', '#7C2D12', '#6D28D9', '#BE185D',
];

export function getDepartmentColorMap(departments: string[]): Record<string, string> {
  const map: Record<string, string> = {};
  (departments || []).forEach((dept, i) => {
    map[dept] = DEPARTMENT_PALETTE[i % DEPARTMENT_PALETTE.length];
  });
  return map;
}
export const CENTER_NODE_COLOR = '#FF0000';

export const COLORS = {
  primary: '#3498db',
  text: '#1a202c',
  textSecondary: '#4a5568',
  background: '#ffffff',
  border: '#e2e8f0',
} as const;

// Edge density presets (fraction of edges to show)
export const EDGE_DENSITY = {
  SPARSE: 0.15,
  NORMAL: 0.30,
  DENSE: 0.60,
  ALL: 1.0,
} as const;

// Adaptive physics presets by node count
export function getPhysicsPreset(nodeCount: number): PhysicsPreset {
  if (nodeCount <= 20) {
    return { chargeStrength: -120, linkDistance: 180, linkStrength: 0.4, collisionRadius: 20 };
  }
  if (nodeCount <= 200) {
    return { chargeStrength: -60, linkDistance: 100, linkStrength: 0.15, collisionRadius: 14 };
  }
  if (nodeCount <= 1000) {
    return { chargeStrength: -25, linkDistance: 60, linkStrength: 0.05, collisionRadius: 8 };
  }
  return { chargeStrength: -10, linkDistance: 30, linkStrength: 0.02, collisionRadius: 5 };
}

// Community hull fill colors
export const COMMUNITY_COLORS: readonly string[] = [
  'rgba(59, 130, 246, 0.12)',
  'rgba(16, 185, 129, 0.12)',
  'rgba(245, 158, 11, 0.12)',
  'rgba(239, 68, 68, 0.12)',
  'rgba(139, 92, 246, 0.12)',
  'rgba(236, 72, 153, 0.12)',
  'rgba(14, 165, 233, 0.12)',
  'rgba(34, 197, 94, 0.12)',
];

// Community hull stroke colors (matching hues, higher opacity)
export const COMMUNITY_STROKE_COLORS: readonly string[] = [
  'rgba(59, 130, 246, 0.30)',
  'rgba(16, 185, 129, 0.30)',
  'rgba(245, 158, 11, 0.30)',
  'rgba(239, 68, 68, 0.30)',
  'rgba(139, 92, 246, 0.30)',
  'rgba(236, 72, 153, 0.30)',
  'rgba(14, 165, 233, 0.30)',
  'rgba(34, 197, 94, 0.30)',
];

// Whether to pin backend positions for large graphs
export function shouldPinPositions(nodeCount: number): boolean {
  return nodeCount >= 500;
}
